import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';

import { BehaviorSubject, Subject } from 'rxjs';
import {
  consultationExtracliniqueData,
  profileCard,
  roomFinalData,
} from '../components/day-program-recap/day-program-interfaces';
import { RoomDal } from '../dal/room.dal';
import { roomWithDoctorAgendas } from '../interfaces/room.interfaces';
import { dailyNeeds } from '../models/anesthNeed.model';
import { Room } from '../models/room.model';

@Injectable({
  providedIn: 'root',
})
export class RoomService {
  public updateProfileReasonMapStart: Subject<{ profileId: string; mergedModifiedPrograms: any }> = new Subject<{
    profileId: string;
    mergedModifiedPrograms: any;
  }>();

  public reloadSmartPlanning: Subject<void> = new Subject<void>();

  public highlightedProfileSmartPlanning: BehaviorSubject<profileCard | undefined> = new BehaviorSubject<
    profileCard | undefined
  >(undefined);

  public fullScreenSmartPlanning: Subject<void> = new Subject<void>();

  public splitRoomSmartPlanning: Subject<{ id: string; isBloc: boolean; mode: string }> = new Subject<{
    id: string;
    isBloc: boolean;
    mode: string;
  }>();

  public emptyRoomSmartPlanning: Subject<{
    data: roomFinalData | consultationExtracliniqueData;
    isBloc: boolean;
    programIndex: number;
    shouldShowProfilesOnNonSplitTeam?: any;
    shouldShowProfilesOnPrograms?: any;
    shouldShowProfilesOnSurgeonOpenings?: any;
  }> = new Subject<{
    data: roomFinalData | consultationExtracliniqueData;
    isBloc: boolean;
    programIndex: number;
    shouldShowProfilesOnNonSplitTeam?: any;
    shouldShowProfilesOnPrograms?: any;
    shouldShowProfilesOnSurgeonOpenings?: any;
  }>();

  public updateEmptyRoomSmartPlanning: Subject<{
    data: roomFinalData | consultationExtracliniqueData;
    isBloc: boolean;
  }> = new Subject<{ data: roomFinalData | consultationExtracliniqueData; isBloc: boolean }>();

  public updateRoomSmartPlanning: Subject<{ roomId: string | undefined; card: any }> = new Subject<{
    roomId: string | undefined;
    card: any;
  }>();

  public updateEmptyRoomOnSplitSmartPlanning: Subject<{
    data: roomFinalData | consultationExtracliniqueData;
    isBloc: boolean;
  }> = new Subject<{ data: roomFinalData | consultationExtracliniqueData; isBloc: boolean }>();

  public refreshAvailableProfilesSmartPlanning: Subject<{ card: profileCard; removing: boolean } | undefined> =
    new Subject<{ card: profileCard; removing: boolean } | undefined>();

  public removeCardSmartPlanning: Subject<{ profile: profileCard; room: roomFinalData; programIndex: number }> =
    new Subject<{ profile: profileCard; room: roomFinalData; programIndex: number }>();

  public updateRankingsSmartPlanning: Subject<{
    anesthsRankings: { seniorsJuniorsRanks: any[]; internesRanks: any[] } | undefined;
    nursesRankings: any[] | undefined;
  }> = new Subject<{
    anesthsRankings: { seniorsJuniorsRanks: any[]; internesRanks: any[] } | undefined;
    nursesRankings: any[] | undefined;
  }>();

  public reloadObjectifStatusSmartPlanning: Subject<void> = new Subject<void>();

  public loadedSmartPlanning: Subject<void> = new Subject<void>();

  public refreshChangesCard: Subject<string | undefined> = new Subject<string | undefined>();

  public clearAvailableProfiles: Subject<void> = new Subject<void>();

  public onUpdateFormationEffectifNeeds: Subject<any> = new Subject<any>();

  constructor(private roomDal: RoomDal) {}

  getNumberofRooms(hospitalId?: string): Observable<number> {
    return this.roomDal.getNumberofRooms(hospitalId);
  }

  getAllRooms(): Observable<Room[]> {
    return this.roomDal.getAllRooms();
  }

  getAllRoomsWithPrograms(jour: string, allRooms: boolean = true): Observable<any[]> {
    return this.roomDal.getAllRoomsWithPrograms(jour, allRooms);
  }

  getHospitalRoomsDayDoctorAgendas(day: string, roomID?: string): Observable<roomWithDoctorAgendas[]> {
    return this.roomDal.getHospitalRoomsDayDoctorAgendas(day, roomID);
  }

  getHospitalRoomsDayRealPrograms(day: string): Observable<Room[]> {
    return this.roomDal.getHospitalRoomsDayRealPrograms(day);
  }

  getHospitalRoomsDaysSurgeonOpenings(day: string, allHospitals?: boolean): Observable<any[]> {
    return this.roomDal.getHospitalRoomsDaysSurgeonOpenings(day, allHospitals);
  }

  getHospitalRoomsDaysSurgeonOpeningsFromTo(startDate: string, endDate: string): Observable<any[]> {
    return this.roomDal.getHospitalRoomsDaysSurgeonOpeningsFromTo(startDate, endDate);
  }

  getHospitalRoomsDaysSurgeonOpeningsClosedFromTo(
    startDate: string,
    endDate: string,
    concernedRooms: string[]
  ): Observable<any[]> {
    return this.roomDal.getHospitalRoomsDaysSurgeonOpeningsClosedFromTo(startDate, endDate, concernedRooms);
  }

  getHospitalRoomsWeekDayConfg(day: number, week: number, tvo?: string): Observable<Room[]> {
    return this.roomDal.getHospitalRoomsWeekDayConfg(day, week, tvo);
  }

  getHospitalRoomsWeekConfig(week: number, tvo?: string): Observable<Room[]> {
    return this.roomDal.getHospitalRoomsWeekConfig(week, tvo);
  }

  updateHospitalRoomsWeekDayConfg(weekDay: number, week: number, surgeonOpenings: any[]): Observable<Room[]> {
    return this.roomDal.updateHospitalRoomsWeekDayConfg(weekDay, week, surgeonOpenings);
  }

  updateManyRoomsSurgeonOpenings(
    date: string,
    rooms: { roomId: string; openings: any[]; programs?: any[] }[],
    notify?: boolean
  ): Observable<any> {
    return this.roomDal.updateManyRoomsSurgeonOpenings(date, rooms, notify);
  }

  getAvailableRooms(day: string): Observable<Room[]> {
    return this.roomDal.getAvailableRooms(day);
  }

  deleteSO(openingId: string): Observable<any> {
    return this.roomDal.deleteSO(openingId);
  }

  deleteSOH(sohToDelete: any[]): Observable<any> {
    return this.roomDal.deleteSOH(sohToDelete);
  }

  updateSOH(sohToUpdate: any[]): Observable<any> {
    return this.roomDal.updateSOH(sohToUpdate);
  }

  addRoom(room: Room): Observable<Room> {
    return this.roomDal.addRoom(room);
  }

  modifyRoom(room: Room): Observable<Room> {
    return this.roomDal.modifyRoom(room);
  }

  archiveRoom(idRoom: string): Observable<Room> {
    return this.roomDal.archiveRoom(idRoom);
  }

  softDeleteRoom(idRoom: string) {
    return this.roomDal.softDeleteRoom(idRoom);
  }

  getRoom(idRoom: string): Observable<Room> {
    return this.roomDal.getRoom(idRoom);
  }

  getHospitalRooms(): Observable<Room[]> {
    return this.roomDal.getHospitalRooms();
  }

  getHospitalRoomsWithSurgeonActivity(startDate: string, endDate: string, surgeonId: string): Observable<Room[]> {
    return this.roomDal.getHospitalRoomsWithSurgeonActivity(startDate, endDate, surgeonId);
  }

  getAnesthFixNeeds(day: number, week: number, tvo: string, period: string): Observable<any[]> {
    return this.roomDal.getAnesthFixNeeds(day, week, tvo, period);
  }

  updateAnesthFixNeeds(day: number, week: number, tvo: string, needs: any[], period: string): Observable<any[]> {
    return this.roomDal.updateAnesthFixNeeds(day, week, tvo, needs, period);
  }

  getAnesthDailyNeeds(weekDay: string, period: string): Observable<dailyNeeds[]> {
    return this.roomDal.getAnesthDailyNeeds(weekDay, period);
  }

  updateAnesthDailyNeeds(weekDay: string, needs: any[]): Observable<any[]> {
    return this.roomDal.updateAnesthDailyNeeds(weekDay, needs);
  }

  clear(): void {
    this.updateProfileReasonMapStart.complete();
    this.updateProfileReasonMapStart = new Subject<{ profileId: string; mergedModifiedPrograms: any }>();

    this.reloadSmartPlanning.complete();
    this.reloadSmartPlanning = new Subject<void>();

    this.highlightedProfileSmartPlanning.complete();
    this.highlightedProfileSmartPlanning = new BehaviorSubject<profileCard | undefined>(undefined);

    this.fullScreenSmartPlanning.complete();
    this.fullScreenSmartPlanning = new Subject<void>();

    this.splitRoomSmartPlanning.complete();
    this.splitRoomSmartPlanning = new Subject<{ id: string; isBloc: boolean; mode: string }>();

    this.emptyRoomSmartPlanning.complete();
    this.emptyRoomSmartPlanning = new Subject<{
      data: roomFinalData | consultationExtracliniqueData;
      isBloc: boolean;
      programIndex: number;
    }>();

    this.updateEmptyRoomSmartPlanning.complete();
    this.updateEmptyRoomSmartPlanning = new Subject<{
      data: roomFinalData | consultationExtracliniqueData;
      isBloc: boolean;
    }>();

    this.updateRoomSmartPlanning.complete();
    this.updateRoomSmartPlanning = new Subject<{ roomId: string | undefined; card: any }>();

    this.updateEmptyRoomOnSplitSmartPlanning.complete();
    this.updateEmptyRoomOnSplitSmartPlanning = new Subject<{
      data: roomFinalData | consultationExtracliniqueData;
      isBloc: boolean;
    }>();

    this.refreshAvailableProfilesSmartPlanning.complete();
    this.refreshAvailableProfilesSmartPlanning = new Subject<{ card: profileCard; removing: boolean } | undefined>();

    this.removeCardSmartPlanning.complete();
    this.removeCardSmartPlanning = new Subject<{ profile: profileCard; room: roomFinalData; programIndex: number }>();

    this.updateRankingsSmartPlanning.complete();
    this.updateRankingsSmartPlanning = new Subject<{
      anesthsRankings: { seniorsJuniorsRanks: any[]; internesRanks: any[] } | undefined;
      nursesRankings: any[] | undefined;
    }>();

    this.reloadObjectifStatusSmartPlanning.complete();
    this.reloadObjectifStatusSmartPlanning = new Subject<void>();

    this.loadedSmartPlanning.complete();
    this.loadedSmartPlanning = new Subject<void>();

    this.refreshChangesCard.complete();
    this.refreshChangesCard = new Subject<string | undefined>();

    this.clearAvailableProfiles.complete();
    this.clearAvailableProfiles = new Subject<void>();
  }
}
